import { useEffect, useState } from "react";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useToast } from "@/components/ui/use-toast";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import { useOrganizationStore } from "@/hooks/useOrganizationStore";
import { useAuthStore } from "@/hooks/useAuthStore";

export const OrganizationSelect = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const {
    userOrganizations,
    setCurrentOrganization,
    currentOrganization,
    isLoading,
    isReady,
    refetch,
  } = useOrganizationContext();
  const orgStore = useOrganizationStore();
  const authStore = useAuthStore();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [pendingOrgId, setPendingOrgId] = useState<string | null>(null);

  useEffect(() => {
    // Only notify once loading is settled and context is ready,
    // and only if we still have no organizations and none is selected.
    if (
      isReady &&
      !isLoading &&
      !currentOrganization &&
      (!userOrganizations || userOrganizations.length === 0)
    ) {
      toast({
        variant: "destructive",
        title: t.common.error,
        description: t.forms.login.noOrganizations,
      });
    }
  }, [isReady, isLoading, currentOrganization, userOrganizations, toast, t]);

  // Watch for newly created organization to become available
  useEffect(() => {
    if (pendingOrgId && userOrganizations.length > 0) {
      const newOrg = userOrganizations.find((org) => org.id === pendingOrgId);
      if (newOrg) {
        setCurrentOrganization(newOrg);
        setPendingOrgId(null);
      }
    }
  }, [pendingOrgId, userOrganizations, setCurrentOrganization]);

  const handleOrgChange = (orgId: string) => {
    if (orgId === "__create_new__") {
      setIsDialogOpen(true);
      // Reset select value to current organization to prevent it from changing
      // The select is controlled, so this prevents the value from updating
      return;
    }
    const selectedOrg = userOrganizations.find((org) => org.id === orgId);
    if (selectedOrg) {
      setCurrentOrganization(selectedOrg);
      // Don't auto-navigate, let user choose where to go
      toast({
        title: t.common.organizationSelected,
        description: t.auth.switchedToOrganization.replace("{organizationName}", selectedOrg.name),
      });
    }
  };

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!orgName.trim() || orgName.trim().length < 2) {
      toast({
        variant: "destructive",
        title: t.common.invalidName,
        description: t.common.organizationNameMinLength,
      });
      return;
    }

    const userId = authStore.userProfileId;
    if (!userId) {
      toast({
        variant: "destructive",
        title: t.common.error,
        description: t.auth.userNotFound,
      });
      return;
    }

    setIsCreating(true);
    try {
      const result = await orgStore.createOrganization(orgName.trim(), userId, "onboarding");

      if (result.success && result.orgId) {
        // Refresh auth session to update JWT claims
        await authStore.loadSession();

        // Set pending org ID - useEffect will watch for it and set it as current
        setPendingOrgId(result.orgId);

        // Refresh organizations list
        await refetch();

        setIsDialogOpen(false);
        setOrgName("");

        toast({
          title: t.common.success,
          description: t.auth.organizationCreated.replace("{orgName}", orgName.trim()),
        });
      } else {
        throw new Error(orgStore.error || "Failed to create organization");
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t.common.error,
        description: error instanceof Error ? error.message : t.forms.error.unexpectedError,
      });
    } finally {
      setIsCreating(false);
    }
  };

  if (!userOrganizations?.length) {
    return null;
  }

  return (
    <Select onValueChange={handleOrgChange} value={currentOrganization?.id || undefined}>
      <SelectTrigger className="w-[250px]">
        <SelectValue placeholder={t.forms.login.selectOrganization} />
      </SelectTrigger>
      <SelectContent>
        {userOrganizations.map((org) => (
          <SelectItem key={org.id} value={org.id}>
            {org.name}
          </SelectItem>
        ))}
        <SelectSeparator />
        <SelectItem value="__create_new__">
          <div className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            <span>{t.auth.createNewOrganization}</span>
          </div>
        </SelectItem>
      </SelectContent>
      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setOrgName("");
            setPendingOrgId(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.auth.createNewOrganization}</DialogTitle>
            <DialogDescription>{t.auth.createOrgDescription}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateOrg}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="org-name">{t.common.organizationName}</Label>
                <Input
                  id="org-name"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder={t.common.organizationNamePlaceholder}
                  disabled={isCreating}
                  autoFocus
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  setOrgName("");
                  setPendingOrgId(null);
                }}
                disabled={isCreating}
              >
                {t.common.cancel}
              </Button>
              <Button type="submit" disabled={isCreating || !orgName.trim()}>
                {isCreating ? t.common.creating : t.common.create}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Select>
  );
};
